import React, { useEffect, useState } from 'react'
import * as R from 'ramda'
import { Order } from './model-order'
import { Restaurant } from './model-restaurant'
import { Channel } from './model-channel'

import { Panel, Line } from './fragments/comp/panel'

import moment, { Moment } from 'moment'

interface IntegrationInfoProps {
  order: Order
  channel: Channel
  restaurant: Restaurant
}

export default function IntegrationInfo(props: IntegrationInfoProps) {
  const [curTime, setCurTime] = useState<string>(formatMomentTime(moment().utcOffset(props.restaurant.timezone.offset)))

  useEffect(() => {
    const tick = setInterval(() => {
      const time = moment().utcOffset(props.restaurant.timezone.offset)
      setCurTime(formatMomentTime(time).replace(/:/, time.seconds() % 2 == 0 ? ':' : ' '))
    }, 1000)
    return () => clearInterval(tick)
  }, [])

  const serviceLabel = props.order.carts.settings.service.label
  let mailTo = null
  let contact = null

  const service =
    props.restaurant.services.find((s) => s.id == props.order.carts.settings.service.id) ||
    R.head(props.restaurant.services)

  if (service && service.contact) {
    contact = service.contact
    mailTo =
      contact && contact.email
        ? `mailto:${contact.email}?subject=${encodeURIComponent(`Order ${props.order.uuid}`)}`
        : null
  }

  const cpURL =
    process.env.NODE_ENV == 'production'
      ? 'http://restaurants.zuppler.com'
      : 'http://restaurants.biznettechnologies.com'

  const chefURL = process.env.NODE_ENV == 'production' ? 'https://chef.zuppler.com' : 'https://zuppler-chef.netlify.app'

  return (
    <div className="row">
      <div className="col-12">
        <Panel id="restaurant" label="Restaurant">
          <Line label="Name">
            {props.restaurant.name} [{' '}
            {props.restaurant.settings.pause_online_ordering ? (
              <span className="text-danger">Paused</span>
            ) : (
              <span className="text-success">Live</span>
            )}{' '}
            ]
          </Line>
          <Line
            label={
              <>
                <span className="fa fa-cogs"></span> Manage restaurant
              </>
            }>
            <ul>
              <li>
                <a target="_blank" href={`${chefURL}/#/onboarding/restaurant/${props.restaurant.id}`} rel="noreferrer">
                  Zuppler Chef
                </a>{' '}
              </li>
              <li>
                <a target="_blank" href={`${cpURL}/restaurants/${props.restaurant.permalink}`} rel="noreferrer">
                  Control Panel (deprecated)
                </a>{' '}
              </li>
            </ul>
          </Line>
          <Line label={'Channel Info'}>
            <ul>
              <li>Name: {props.channel.name}</li>
              <li>
                URL:{' '}
                <a href={props.channel.url} target="_blank" rel="noreferrer">
                  {props.channel.url}
                </a>{' '}
              </li>
              {!props.order.carts.integration_url
                .replace(/https?:\/\//, '')
                .includes(props.channel.url.replace(/https?:\/\//, '')) && (
                <li>
                  <span className="text-danger">Order came from an invalid URL. Please review configuration!!!</span>
                </li>
              )}
              <li>Permalink: {props.channel.permalink}</li>
              <li>
                <a target="_blank" href={`${cpURL}/channels/${props.channel.permalink}`} rel="noreferrer">
                  <span className="fa fa-cogs"></span> Configure with Control Panel
                </a>
              </li>
            </ul>
          </Line>
          <Line label="Integration">
            <a href={props.order.carts.integration_url} target="_blank" rel="noreferrer">
              {props.order.carts.integration_url}
            </a>
            <br />
            Remote ID: {props.channel.integrations[0].remote_id}
          </Line>
          <Line label="Current Time">
            {curTime} ( {props.restaurant.timezone.name} )
          </Line>
          {!moment().isSame(props.order.carts.settings.time.value, 'day') && (
            <Line label="Today Hours of Operation">
              <HoursOfOperations
                refDate={moment()}
                zoneOffset={props.restaurant.timezone.offset}
                data={props.restaurant.hours_of_operation}
              />
            </Line>
          )}
          <Line label="Order Date Hours of Operation">
            <HoursOfOperations
              refDate={moment(props.order.carts.settings.time.value)}
              zoneOffset={props.restaurant.timezone.offset}
              data={props.restaurant.hours_of_operation}
            />
          </Line>
          {contact && (
            <Line label={`${serviceLabel} Contact`}>
              {contact.name} @ <a href={`tel:${contact.phone}`}>{contact.phone}</a>
            </Line>
          )}
          {mailTo && contact && (
            <Line label={`${serviceLabel} Email`}>
              <a href={mailTo}>{contact.email}</a>
            </Line>
          )}
        </Panel>
      </div>
    </div>
  )
}

function HoursOfOperations(props: { refDate: Moment; zoneOffset: number; data: number[][][] }) {
  const dow = props.refDate.day()
  const intervals = R.filter(([start, stop]) => start < stop && start != 0, props.data[dow])
  const joinedIntervals = R.reduce(
    (prev, [start, stop]) => {
      if (prev && prev.length > 0) {
        const [pStart, pStop] = R.last(prev) as number[]
        if (pStop == start) {
          return R.append([pStart, stop], R.take(prev.length - 1, prev))
        } else {
          return R.append([start, stop], prev)
        }
      } else {
        return [start, stop]
      }
    },
    [R.head(intervals)],
    R.tail(intervals)
  )

  if (joinedIntervals.length > 0) {
    const joinedMomentIntervals = joinedIntervals
      .filter((n?: number[]) => !!n && n.length == 2)
      .map(([start, stop]: number[]) => [toMoment(props.zoneOffset, start), toMoment(props.zoneOffset, stop)])
      .map(([start, stop]: Moment[]) => [formatMomentTime(start), formatMomentTime(stop, start)])
      .map(([start, stop]: string[]) => `${start} - ${stop}`)
      .join('; ')

    return (
      <>
        {props.refDate.format('ddd')}: {joinedMomentIntervals || 'Closed'}
      </>
    )
  } else {
    return <>Closed</>
  }
}

const formatMomentTime = (m: Moment, ref?: Moment): string =>
  ref && ref.isSameOrBefore(m) ? m.format('LT') + ' (next day)' : m.format('LT')

const toMoment = (zoneOffset: number, startMin: number) => {
  const m = moment()
  m.utcOffset(zoneOffset)
  m.hours(Math.floor(startMin % 1440) / 60)
  m.minutes(startMin % 60)
  return m
}
